/**
 * A single ledger transaction, and the correction chain around it.
 *
 * **Nothing here is editable, and there is no action that could be.** A correction is a
 * compensating entry posted by the service that owns the event, with a reason and a second pair
 * of eyes — not something typed into a console. The screen says so rather than showing a disabled
 * button, because a greyed-out "Post correction" implies the capability exists and somebody merely
 * lacks it.
 *
 * **The chain is shown both directions.** What this transaction reverses, and what reverses it. A
 * view that followed only one way would present a corrected entry as though nothing had corrected
 * it, which is the single most misleading thing a journal reader can be told.
 *
 * **Every figure is the server's.** The legs, the totals and the balance check are rendered as
 * they arrive. The one arithmetic this screen does is comparing the two sides, and it does that
 * to *display* an integrity warning — never to compute a number somebody would then quote.
 */
import { Button, StatusChip } from '../components/primitives';
import { BookingLedgerLines, DataTable } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Money, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useBookingLedger,
  useJournalDetail,
  usePanditStatement,
  type JournalRow,
} from '../lib/api/hooks/ledger';
import { formatDateTime } from '../lib/datetime';

/**
 * Whether a journal's own legs balance.
 *
 * The server refuses an unbalanced posting, so this can only ever fire on a rendering or
 * transport fault — which is exactly why it is worth showing. A money screen that trusts its
 * input silently is one that will one day display a broken journal as a normal one.
 */
export function balanceOf(row: JournalRow): { debit: number; credit: number; balanced: boolean } {
  const debit = row.legs
    .filter((leg) => leg.side === 'debit')
    .reduce((sum, leg) => sum + leg.amount_paise, 0);
  const credit = row.legs
    .filter((leg) => leg.side === 'credit')
    .reduce((sum, leg) => sum + leg.amount_paise, 0);
  return { debit, credit, balanced: debit === credit };
}

function Journal({ row, muted }: { row: JournalRow; muted?: boolean }) {
  const sums = balanceOf(row);
  return (
    <Section
      title={row.txn_type}
      note={`#${row.id} · ${formatDateTime(row.occurred_at)}`}
      style={muted ? { opacity: 0.8 } : undefined}
      pad={false}
    >
      {!sums.balanced && (
        <ErrorState
          title="This journal does not balance"
          cause={`Debits ${sums.debit} paise against credits ${sums.credit} paise. The server refuses to post an unbalanced entry, so seeing one here means the record did not arrive intact.`}
          inline
        />
      )}
      <DataTable
        density="compact"
        columns={[
          { key: 'code', label: 'Code', mono: true },
          { key: 'account', label: 'Account' },
          { key: 'side', label: 'Side' },
          { key: 'pandit', label: 'Pandit', mono: true },
          { key: 'amount', label: 'Amount', align: 'right' },
          { key: 'memo', label: 'Memo' },
        ]}
        rows={row.legs.map((leg, index) => ({
          id: `${row.id}-${index}`,
          code: leg.account_code,
          account: leg.account_name,
          side: leg.side,
          pandit: leg.pandit_profile_id ? `#${leg.pandit_profile_id}` : '—',
          amount: <Money paise={leg.amount_paise} />,
          memo: leg.memo || '—',
        }))}
      />
    </Section>
  );
}

/** April-to-March, the year an Indian accountant works in, for a date inside it. */
export function financialYear(when: Date): string {
  const year = when.getMonth() >= 3 ? when.getFullYear() : when.getFullYear() - 1;
  return `${year}-${String((year + 1) % 100).padStart(2, '0')}`;
}

/** The pandit a journal is about, if any — a sub-ledger leg is the only place one appears. */
export function panditOf(row: JournalRow): number | null {
  return row.legs.find((leg) => leg.pandit_profile_id)?.pandit_profile_id ?? null;
}

function BookingMoney({ bookingId }: { bookingId: number }) {
  const trail = useBookingLedger(bookingId);
  if (trail.isPending) return <LoadingState message="Reading the booking's money…" />;
  if (trail.isError) {
    return (
      <ErrorState title="Could not read the booking's money" cause={errorMessage(trail.error)} />
    );
  }
  return <BookingLedgerLines lines={trail.data.lines} />;
}

function PanditLedger({ panditProfileId, fy }: { panditProfileId: number; fy: string }) {
  const statement = usePanditStatement(panditProfileId, fy);
  if (statement.isPending) return <LoadingState message="Reading the statement…" />;
  if (statement.isError) {
    return (
      <ErrorState title="Could not read the statement" cause={errorMessage(statement.error)} />
    );
  }
  return (
    <Section
      title={`Pandit #${panditProfileId} · ${fy}`}
      note="the same figures the pandit's own earnings screen shows"
    >
      <KV
        rows={[
          ['Opening', <Money key="o" paise={statement.data.opening_paise} />],
          ['Movements', String(statement.data.lines.length)],
          ['Closing', <Money key="c" paise={statement.data.closing_paise} bold />],
        ]}
      />
    </Section>
  );
}

export function LedgerTransaction() {
  const { params, go } = useConsole();
  const transactionId = Number(params.txnId);
  const detail = useJournalDetail(Number.isFinite(transactionId) ? transactionId : null);

  if (detail.isPending) return <LoadingState message="Reading the transaction…" />;
  if (detail.isError) {
    return (
      <ErrorState title="Could not read that transaction" cause={errorMessage(detail.error)} />
    );
  }

  const { row, chain } = detail.data;

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="secondary" size="sm" onClick={() => go('ledger')}>
          ← Back to the journal
        </Button>
        <StatusChip
          tone={row.reverses_ledger_transaction_id ? 'warn' : 'neutral'}
          label={row.reverses_ledger_transaction_id ? 'correction' : 'original entry'}
        />
      </div>

      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        This entry cannot be edited or deleted. If it is wrong, the service that posted it posts a
        compensating entry referencing it — with a reason, and a second pair of eyes. There is no
        console action for that, deliberately.
      </p>

      <KV
        rows={[
          ['Event', row.txn_type],
          ['Occurred', formatDateTime(row.occurred_at)],
          ['Recorded', formatDateTime(row.created_at)],
          ['Booking', row.booking_public_code ?? '—'],
          ['Posted by', row.posted_by === 'admin' ? `admin #${row.posted_by_admin_id}` : 'the system'],
          ['Traced to', row.idempotency_key],
          ['Memo', row.memo || '—'],
        ]}
      />

      <Journal row={row} />

      <Section title="Correction chain" note={`${chain.length} linked`}>
        {chain.length === 0 ? (
          <EmptyState
            icon="scale"
            title="Nothing has corrected this"
            body="No compensating entry references it, and it references none."
            inline
          />
        ) : (
          <p style={{ margin: 0, fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
            Both directions: what this reverses, and what reverses it.
          </p>
        )}
      </Section>

      {chain.map((linked) => (
        <Journal key={linked.id} row={linked} muted />
      ))}

      {row.booking_id !== null && <BookingMoney bookingId={row.booking_id} />}

      {panditOf(row) !== null && (
        <PanditLedger
          panditProfileId={panditOf(row)!}
          fy={financialYear(new Date(row.occurred_at))}
        />
      )}
    </>
  );
}
